import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trophy, Timer, ArrowLeft, RotateCcw, Play as PlayIcon, Sparkles, AlertTriangle, Volume2, VolumeX } from "lucide-react";
import SEO from "@/components/site/SEO";
import { popConfetti, fireworks, burstFromElement } from "@/lib/confetti";
import { toast } from "sonner";
import ShareAndUnlock from "@/components/sections/ShareAndUnlock";

/**
 * Honk Patrol — a 60-second arcade game
 * Grid of parking stalls. Cars spawn randomly:
 *  - "wrong"  → red, tilted, blocking — tap to honk (+10)
 *  - "fine"   → blue, straight — DON'T tap (tap = -5)
 *  - empty    → tap = -2 (missed)
 * Round lasts 60s. Speed ramps up every 15s.
 */

const ROWS = 3;
const COLS = 4;
const TOTAL = ROWS * COLS;
const ROUND_MS = 60_000;
const STORAGE_BEST = "secqre.honk.best";
const STORAGE_PLAYS = "secqre.honk.plays";

const STAGES = [
  { from: 0, spawnMs: 950, wrongRatio: 0.45, wrongLifetime: 2200 },
  { from: 15_000, spawnMs: 800, wrongRatio: 0.5, wrongLifetime: 1900 },
  { from: 30_000, spawnMs: 650, wrongRatio: 0.55, wrongLifetime: 1600 },
  { from: 45_000, spawnMs: 520, wrongRatio: 0.6, wrongLifetime: 1300 },
];

function currentStage(elapsed) {
  let s = STAGES[0];
  for (const st of STAGES) if (elapsed >= st.from) s = st;
  return s;
}

const CAR_COLORS_FINE = ["#0F172A", "#1E40AF", "#0E7490", "#334155", "#6D28D9"];
const CAR_COLORS_WRONG = ["#E63946", "#DC2626", "#F97316"];

let idCounter = 1;
const nextId = () => idCounter++;

export default function Play() {
  const [phase, setPhase] = useState("idle"); // idle | playing | over
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(ROUND_MS);
  const [stalls, setStalls] = useState(() => Array.from({ length: TOTAL }, () => null));
  const [hits, setHits] = useState({ honked: 0, mistakes: 0, missed: 0 });
  const [combo, setCombo] = useState(0);
  const [muted, setMuted] = useState(true);
  const [best, setBest] = useState(0);

  const stallsRef = useRef(stalls);
  stallsRef.current = stalls;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const startedAtRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Load best
  useEffect(() => {
    try {
      const b = parseInt(localStorage.getItem(STORAGE_BEST) || "0", 10);
      setBest(isNaN(b) ? 0 : b);
    } catch (_) {}
  }, []);

  // Sound helper (WebAudio, no external file)
  const beep = useCallback((freq = 880, durationMs = 70, type = "square", gain = 0.06) => {
    if (muted) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = freq;
      g.gain.value = gain;
      o.connect(g).connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + durationMs / 1000);
    } catch (_) {}
  }, [muted]);

  const reset = () => {
    setScore(0);
    setTimeLeft(ROUND_MS);
    setStalls(Array.from({ length: TOTAL }, () => null));
    setHits({ honked: 0, mistakes: 0, missed: 0 });
    setCombo(0);
  };

  const start = () => {
    reset();
    startedAtRef.current = Date.now();
    setPhase("playing");
    try {
      const plays = parseInt(localStorage.getItem(STORAGE_PLAYS) || "0", 10);
      localStorage.setItem(STORAGE_PLAYS, String((isNaN(plays) ? 0 : plays) + 1));
    } catch (_) {}
  };

  // Tick timer
  useEffect(() => {
    if (phase !== "playing") return;
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      const remaining = Math.max(0, ROUND_MS - elapsed);
      setTimeLeft(remaining);
      if (remaining === 0) {
        setPhase("over");
      }
    }, 100);
    return () => clearInterval(id);
  }, [phase]);

  // Game over: persist best
  useEffect(() => {
    if (phase !== "over") return;
    setScore((s) => {
      try {
        const prev = parseInt(localStorage.getItem(STORAGE_BEST) || "0", 10);
        if (s > (isNaN(prev) ? 0 : prev)) {
          localStorage.setItem(STORAGE_BEST, String(s));
          setBest(s);
          fireworks();
          toast.success("New high score!", { description: `Beating your old ${prev || 0}` });
        }
      } catch (_) {}
      return s;
    });
  }, [phase]);

  // Spawner
  useEffect(() => {
    if (phase !== "playing") return;
    let cancelled = false;

    const tick = () => {
      if (cancelled || phaseRef.current !== "playing") return;
      const elapsed = Date.now() - startedAtRef.current;
      const stage = currentStage(elapsed);

      // Pick an empty stall
      setStalls((prev) => {
        const emptyIdx = prev.map((c, i) => (c == null ? i : -1)).filter((x) => x >= 0);
        if (emptyIdx.length === 0) return prev;
        const idx = emptyIdx[Math.floor(Math.random() * emptyIdx.length)];
        const isWrong = Math.random() < stage.wrongRatio;
        const lifetime = isWrong ? stage.wrongLifetime : 2800;
        const car = {
          id: nextId(),
          type: isWrong ? "wrong" : "fine",
          color: isWrong
            ? CAR_COLORS_WRONG[Math.floor(Math.random() * CAR_COLORS_WRONG.length)]
            : CAR_COLORS_FINE[Math.floor(Math.random() * CAR_COLORS_FINE.length)],
          spawnedAt: Date.now(),
          lifetime,
          tilt: isWrong ? (Math.random() < 0.5 ? -14 : 14) : (Math.random() * 4 - 2),
        };
        const next = [...prev];
        next[idx] = car;
        return next;
      });

      const delay = stage.spawnMs + (Math.random() * 250 - 125);
      setTimeout(tick, Math.max(200, delay));
    };

    const t = setTimeout(tick, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [phase]);

  // Auto-expire cars
  useEffect(() => {
    if (phase !== "playing") return;
    const id = setInterval(() => {
      const now = Date.now();
      let missedDelta = 0;
      setStalls((prev) => prev.map((c) => {
        if (!c) return c;
        if (now - c.spawnedAt >= c.lifetime) {
          if (c.type === "wrong") missedDelta += 1;
          return null;
        }
        return c;
      }));
      if (missedDelta > 0) {
        setScore((s) => Math.max(0, s - 5 * missedDelta));
        setHits((h) => ({ ...h, missed: h.missed + missedDelta }));
        setCombo(0);
      }
    }, 120);
    return () => clearInterval(id);
  }, [phase]);

  const handleStallClick = (idx, e) => {
    if (phase !== "playing") return;
    const car = stallsRef.current[idx];
    if (!car) {
      // Empty stall — tiny penalty + low beep
      setScore((s) => Math.max(0, s - 2));
      setCombo(0);
      beep(220, 60, "sine", 0.04);
      return;
    }
    if (car.type === "wrong") {
      const points = 10 + Math.min(combo, 5) * 2;
      setScore((s) => s + points);
      setHits((h) => ({ ...h, honked: h.honked + 1 }));
      setCombo((c) => c + 1);
      beep(1200, 80, "square", 0.06);
      setTimeout(() => beep(1600, 80, "square", 0.05), 60);
      // Confetti from element
      burstFromElement(e?.currentTarget);
      // Remove the car
      setStalls((prev) => {
        const next = [...prev];
        next[idx] = null;
        return next;
      });
    } else {
      // Tapped a correctly parked car — penalty
      setScore((s) => Math.max(0, s - 5));
      setHits((h) => ({ ...h, mistakes: h.mistakes + 1 }));
      setCombo(0);
      beep(180, 200, "sawtooth", 0.05);
      toast("Hey! That driver was fine.", { description: "Only honk the ones blocking you." });
    }
  };

  const seconds = (timeLeft / 1000).toFixed(1);
  const accuracy = useMemo(() => {
    const taps = hits.honked + hits.mistakes;
    if (taps === 0) return 100;
    return Math.round((hits.honked / taps) * 100);
  }, [hits]);

  const rank = useMemo(() => {
    if (score >= 300) return { label: "Parking Marshal", emoji: "🥇" };
    if (score >= 200) return { label: "Lane Captain", emoji: "🥈" };
    if (score >= 120) return { label: "Civic Hero", emoji: "🥉" };
    if (score >= 60) return { label: "Honest Driver", emoji: "🚗" };
    return { label: "Rookie Patroller", emoji: "🔰" };
  }, [score]);

  return (
    <div data-testid="page-play" className="min-h-screen bg-[#F8F9FA]">
      <SEO title="Honk Patrol — Wrong-Parking Arcade" description="A 60-second arcade game from SECQRE. Honk only the wrongly parked cars. Beat your high score." path="/play" />

      <section className="pt-28 md:pt-32 pb-16">
        <div className="container-secqre">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0A0A0A]" data-testid="play-back">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>

          <div className="mt-6 grid lg:grid-cols-12 gap-8">
            {/* Left — header */}
            <div className="lg:col-span-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#FFD600] px-3 py-1.5 text-xs font-bold text-[#0A0A0A]">
                <Sparkles className="w-3.5 h-3.5" /> MINI-GAME
              </div>
              <h1 className="mt-5 font-heading font-black text-5xl md:text-6xl tracking-tight leading-[1.02]" data-testid="play-title">
                Honk <span className="highlight-yellow">Patrol.</span>
              </h1>
              <p className="mt-5 text-[#475569] leading-relaxed">
                Cars roll into the lot. <strong>Red, tilted ones are wrongly parked</strong> —
                tap to honk them off. <strong>Straight, dark cars are fine</strong> — leave them alone.
                You have <strong>60 seconds</strong>.
              </p>

              {/* Scoreboard */}
              <div className="mt-7 grid grid-cols-3 gap-3" data-testid="play-scoreboard">
                <Stat label="Score" value={score} testid="play-score" highlight />
                <Stat label="Time" value={`${seconds}s`} testid="play-time" icon={<Timer className="w-3.5 h-3.5" />} />
                <Stat label="Best" value={best} testid="play-best" icon={<Trophy className="w-3.5 h-3.5" />} />
              </div>

              {combo > 1 && phase === "playing" && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-[#FFD600] px-3 py-1.5 text-xs font-bold animate-pulse-soft" data-testid="play-combo">
                  🔥 {combo}× combo +{Math.min(combo, 5) * 2} bonus
                </div>
              )}

              <div className="mt-7 flex flex-wrap gap-2">
                {phase === "idle" && (
                  <button onClick={start} className="btn-primary" data-testid="play-start">
                    <PlayIcon className="w-4 h-4" /> Start round
                  </button>
                )}
                {phase === "playing" && (
                  <button onClick={() => setPhase("over")} className="btn-ghost" data-testid="play-end">
                    End round
                  </button>
                )}
                {phase === "over" && (
                  <button onClick={start} className="btn-secondary" data-testid="play-replay">
                    <RotateCcw className="w-4 h-4" /> Play again
                  </button>
                )}
                <button
                  onClick={() => setMuted((m) => !m)}
                  className="btn-ghost"
                  aria-label={muted ? "Unmute" : "Mute"}
                  data-testid="play-mute"
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  {muted ? "Sound off" : "Sound on"}
                </button>
              </div>

              <div className="mt-8 rounded-2xl border border-[#E2E8F0] bg-white p-5 text-sm">
                <div className="font-semibold text-[#0A0A0A] mb-3">Scoring</div>
                <ul className="space-y-2 text-[#475569]">
                  <li className="flex items-center gap-2"><Badge color="green">+10</Badge> Honk a wrongly parked car</li>
                  <li className="flex items-center gap-2"><Badge color="navy">+2/combo</Badge> Streak bonus (max +10)</li>
                  <li className="flex items-center gap-2"><Badge color="red">−5</Badge> Honk a correctly parked car</li>
                  <li className="flex items-center gap-2"><Badge color="red">−5</Badge> Let a wrong car escape</li>
                  <li className="flex items-center gap-2"><Badge color="gray">−2</Badge> Tap empty stall</li>
                </ul>
              </div>
            </div>

            {/* Right — game board */}
            <div className="lg:col-span-8">
              <div className="relative rounded-3xl border border-[#E2E8F0] bg-white p-5 md:p-7 overflow-hidden">
                {/* Asphalt grid background */}
                <div aria-hidden className="absolute inset-0 bg-[#1f2937]" />
                <div aria-hidden className="absolute inset-0 opacity-30 grid-bg" />
                <div aria-hidden className="absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-black/50 to-transparent" />

                {/* Yellow "STAFF ONLY" tape across edges */}
                <div aria-hidden className="absolute -left-10 top-6 -rotate-3 px-8 py-1 bg-[#FFD600] text-[#0A0A0A] text-xs font-black tracking-widest font-mono">
                  WEST · LOT · A4 · WEST · LOT · A4
                </div>

                <div
                  className="relative grid gap-4 md:gap-5 aspect-[4/3] max-h-[560px] mx-auto"
                  style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)`, gridTemplateRows: `repeat(${ROWS}, 1fr)` }}
                  data-testid="play-board"
                >
                  {stalls.map((car, i) => (
                    <Stall key={i} index={i} car={car} onClick={(e) => handleStallClick(i, e)} disabled={phase !== "playing"} />
                  ))}
                </div>

                {/* Overlay states */}
                {phase === "idle" && (
                  <Overlay testid="play-idle-overlay">
                    <h2 className="font-heading font-black text-4xl text-white">Ready, patrol?</h2>
                    <p className="text-white/70 mt-2 max-w-md mx-auto">60 seconds. Tap only the cars that are tilted &amp; red. Streak bonuses are real.</p>
                    <button onClick={start} className="btn-secondary mt-6" data-testid="play-start-overlay">
                      <PlayIcon className="w-4 h-4" /> Start round
                    </button>
                  </Overlay>
                )}
                {phase === "over" && (
                  <Overlay testid="play-over-overlay">
                    <div className="text-5xl mb-2">{rank.emoji}</div>
                    <h2 className="font-heading font-black text-4xl text-white">Final score: <span className="text-[#FFD600]">{score}</span></h2>
                    <p className="text-white/80 mt-2">Rank: <strong className="text-white">{rank.label}</strong></p>
                    <div className="mt-5 grid grid-cols-3 gap-3 text-xs">
                      <MiniStat label="Honked" value={hits.honked} />
                      <MiniStat label="Missed" value={hits.missed} />
                      <MiniStat label="Accuracy" value={`${accuracy}%`} />
                    </div>

                    <ShareAndUnlock score={score} rank={rank} accuracy={accuracy} hits={hits} />

                    <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
                      <button onClick={start} className="btn-secondary" data-testid="play-replay-overlay">
                        <RotateCcw className="w-4 h-4" /> Play again
                      </button>
                      <Link to="/pricing" className="btn-ghost" data-testid="play-pricing-cta">
                        Make this real — get a SECQRE
                      </Link>
                    </div>
                  </Overlay>
                )}
              </div>

              <p className="mt-5 text-xs text-[#475569] text-center">
                Pro tip: type <kbd className="px-1.5 py-0.5 bg-white border border-[#E2E8F0] rounded">secqre</kbd> anywhere on the site for a surprise.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon, highlight, testid }) {
  return (
    <div
      className={`rounded-xl border p-3 ${highlight ? "bg-[#FFD600] border-[#FFD600]" : "bg-white border-[#E2E8F0]"}`}
      data-testid={testid}
    >
      <div className={`text-[10px] tracking-widest font-bold uppercase flex items-center gap-1.5 ${highlight ? "text-[#0A0A0A]/70" : "text-[#475569]"}`}>
        {icon}{label}
      </div>
      <div className={`mt-1 font-heading font-black text-2xl tabular-nums ${highlight ? "text-[#0A0A0A]" : "text-[#0A0A0A]"}`}>
        {value}
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/10 border border-white/15 px-3 py-2 text-white">
      <div className="text-[10px] tracking-widest font-bold uppercase opacity-70">{label}</div>
      <div className="font-heading font-bold text-lg">{value}</div>
    </div>
  );
}

function Badge({ color, children }) {
  const map = {
    green: "bg-emerald-500 text-white",
    red: "bg-[#E63946] text-white",
    navy: "bg-[#0F172A] text-[#FFD600]",
    gray: "bg-[#475569] text-white",
  };
  return <span className={`inline-flex items-center justify-center text-[10px] font-bold rounded px-1.5 py-0.5 min-w-[42px] ${map[color] || map.gray}`}>{children}</span>;
}

function Overlay({ children, testid }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center text-center bg-[#0F172A]/90 backdrop-blur-sm rounded-3xl" data-testid={testid}>
      <div className="p-8">{children}</div>
    </div>
  );
}

function Stall({ index, car, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="relative rounded-xl border-2 border-dashed border-white/30 bg-white/[0.04] transition-all hover:bg-white/[0.07] focus:outline-none focus:ring-2 focus:ring-[#FFD600] disabled:cursor-not-allowed"
      data-testid={`play-stall-${index}`}
      aria-label={`Parking stall ${index + 1}${car ? ` ${car.type} car` : " empty"}`}
    >
      {/* Stall lines */}
      <span aria-hidden className="absolute left-2 right-2 top-1 h-0.5 bg-[#FFD600]/40" />
      <span aria-hidden className="absolute left-2 right-2 bottom-1 h-0.5 bg-[#FFD600]/40" />

      {car && <CarSprite car={car} />}
    </button>
  );
}

function CarSprite({ car }) {
  const wrong = car.type === "wrong";
  return (
    <div
      className="absolute inset-0 flex items-center justify-center transition-transform"
      style={{ transform: `rotate(${car.tilt}deg) scale(${wrong ? 1.02 : 1})` }}
      data-car-type={car.type}
    >
      {/* Lifetime bar */}
      <CarLifetimeBar spawnedAt={car.spawnedAt} lifetime={car.lifetime} />

      {/* Car body */}
      <div className="relative w-[78%] h-[62%]">
        <div
          className="absolute inset-0 rounded-md shadow-[0_8px_18px_-6px_rgba(0,0,0,0.6)]"
          style={{ background: car.color }}
        />
        {/* Roof / windshield */}
        <div
          className="absolute left-[18%] right-[18%] top-[12%] bottom-[42%] rounded-sm"
          style={{ background: "rgba(255,255,255,0.55)" }}
        />
        {/* Headlights */}
        <span aria-hidden className="absolute right-[3%] top-[28%] w-[6%] h-[14%] rounded-sm bg-[#fff3a8]" />
        <span aria-hidden className="absolute right-[3%] bottom-[28%] w-[6%] h-[14%] rounded-sm bg-[#fff3a8]" />
        {/* Wheels */}
        <span aria-hidden className="absolute left-[10%] -top-1 w-[14%] h-[18%] rounded bg-black" />
        <span aria-hidden className="absolute right-[10%] -top-1 w-[14%] h-[18%] rounded bg-black" />
        <span aria-hidden className="absolute left-[10%] -bottom-1 w-[14%] h-[18%] rounded bg-black" />
        <span aria-hidden className="absolute right-[10%] -bottom-1 w-[14%] h-[18%] rounded bg-black" />

        {wrong && (
          <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[#FFD600] border-2 border-[#0A0A0A] flex items-center justify-center animate-pulse-soft">
            <AlertTriangle className="w-4 h-4 text-[#0A0A0A]" />
          </div>
        )}
      </div>

      {/* Footer label for wrong */}
      {wrong && (
        <div className="absolute bottom-1 left-1 right-1 text-center text-[9px] font-bold tracking-widest text-[#FFD600] uppercase">
          Wrongly parked — HONK
        </div>
      )}
    </div>
  );
}

function CarLifetimeBar({ spawnedAt, lifetime }) {
  const [pct, setPct] = useState(100);
  useEffect(() => {
    let raf;
    const tick = () => {
      const elapsed = Date.now() - spawnedAt;
      const p = Math.max(0, 100 - (elapsed / lifetime) * 100);
      setPct(p);
      if (p > 0) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [spawnedAt, lifetime]);
  return (
    <div className="absolute top-1.5 left-2 right-2 h-1 rounded-full bg-white/15 overflow-hidden">
      <div
        className="h-full bg-[#FFD600] transition-[width] duration-100"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
